import { useQuery } from '@tanstack/react-query';
import { TCurrencyCode } from 'interfaces';
import { makeBookingManagerApi } from 'services/BookingManagerApi';
import { TAllocatableBookingSortKey, IAllocatableBookingsResponse } from '../types';

export const ALLOCATABLE_BOOKINGS_QUERY_KEY = 'allocatableBookings';

/**
 * React Query hook for fetching bookings available for allocation.
 * Returns bookings with positive outstanding balance for the given TC/currency,
 * plus any bookings specified in includeBookingUuids (for showing previously allocated bookings).
 *
 * @param travelCompanyUuid - The travel company UUID
 * @param currency - The currency code
 * @param sort - Sort configuration (key and order)
 * @param includeBookingUuids - Optional array of booking UUIDs to always include (even if balance is 0)
 * @param enabled - Whether the query should be enabled
 * @returns Query object with allocatable bookings data
 */
export const useAllocatableBookingsQuery = (
  travelCompanyUuid: string | null,
  currency: TCurrencyCode | null,
  sort: { key: TAllocatableBookingSortKey; order: 'asc' | 'desc' } = { key: 'arrivalDate', order: 'asc' },
  includeBookingUuids: string[] = [],
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: [ALLOCATABLE_BOOKINGS_QUERY_KEY, travelCompanyUuid, currency, sort, includeBookingUuids],
    queryFn: async (): Promise<IAllocatableBookingsResponse> => {
      if (!travelCompanyUuid || !currency) {
        return { bookings: [] };
      }
      const api = makeBookingManagerApi();
      const response = await api.getBookingsForAllocation({
        travelCompanyUuid,
        currency,
        sort,
        includeBookingUuids: includeBookingUuids.length > 0 ? includeBookingUuids : undefined,
      });
      return response.data;
    },
    enabled: enabled && !!travelCompanyUuid && !!currency,
  });
};
